/**
 * Connection Guard Utility
 * 
 * Prevents race conditions and duplicate connection attempts
 * that can cause token invalidation and infinite spinners.
 */

import { logger } from '@/services/LoggingService';

class ConnectionGuard {
  private activeConnections = new Set<string>();
  private connectionTimeouts = new Map<string, NodeJS.Timeout>();

  /**
   * Check if a connection is already in progress for a user
   */
  isConnecting(userId: string): boolean {
    return this.activeConnections.has(userId);
  }

  /**
   * Start a connection attempt with timeout protection
   */
  startConnection(userId: string, timeoutMs: number = 30000): boolean {
    if (this.activeConnections.has(userId)) {
      logger.warn('ConnectionGuard', 'Preventing duplicate connection attempt', { userId });
      return false;
    }

    logger.info('ConnectionGuard', 'Starting protected connection', { userId, timeoutMs });
    this.activeConnections.add(userId);

    // Set timeout to automatically clear connection
    const timeout = setTimeout(() => {
      logger.warn('ConnectionGuard', 'Connection timeout reached', { userId, timeoutMs });
      this.endConnection(userId);
    }, timeoutMs);

    this.connectionTimeouts.set(userId, timeout);
    return true;
  }

  /**
   * End a connection attempt and cleanup
   */
  endConnection(userId: string): void {
    logger.info('ConnectionGuard', 'Ending connection attempt', { userId });
    
    this.activeConnections.delete(userId);
    
    const timeout = this.connectionTimeouts.get(userId);
    if (timeout) {
      clearTimeout(timeout);
      this.connectionTimeouts.delete(userId);
    }
  }

  /**
   * Get all active connections (for debugging)
   */
  getActiveConnections(): string[] {
    return Array.from(this.activeConnections);
  }

  /**
   * Force clear all connections (emergency cleanup)
   */
  clearAll(): void {
    logger.warn('ConnectionGuard', 'Force clearing all connections');
    
    // Clear all timeouts
    for (const timeout of this.connectionTimeouts.values()) {
      clearTimeout(timeout);
    }
    
    this.activeConnections.clear();
    this.connectionTimeouts.clear();
  }
}

// Export singleton instance
export const connectionGuard = new ConnectionGuard();